import { QueryClient } from '@tanstack/react-query';
import { isPortalApiError } from './portalProblem';

/**
 * TanStack Query client for the Portal app — mirrors the CRM's own
 * `createQueryClient` defaults, but keyed off `PortalApiError`
 * (`isPortalApiError`), never the CRM's `isQflyError` (a different, CRM-only
 * error class this Portal doesn't throw).
 *
 * Never retrying a 4xx is doubly load-bearing here (state-management-critic,
 * swarm review): without it, a `session_expired` 401 would retry up to 3
 * times by TanStack Query's own default, each retry independently re-hitting
 * the response interceptor — `handleSessionExpired`'s own idempotency guard
 * already protects against redundant navigation, but suppressing the retry
 * here means a session-expiry resolves in exactly one round trip, not up to
 * three.
 */
export const createPortalQueryClient = (): QueryClient =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (isPortalApiError(error) && error.status >= 400 && error.status < 500) {
            return false;
          }
          return failureCount < 2;
        },
      },
      mutations: {
        retry: false,
      },
    },
  });
